import { useRef } from 'react'
import type { Editor } from '@tiptap/react'
import { Space, Button, Tooltip, Dropdown, Divider, message } from 'antd'
import type { MenuProps } from 'antd'
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  PictureOutlined,
  TableOutlined,
  CodeOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { uploadBlogImage, ACCEPTED_IMAGE_TYPES } from './uploadBlogImage'

interface ToolbarProps {
  editor: Editor | null
}

/** A toolbar toggle button that reflects the editor's active mark/node state. */
function MarkButton({
  icon,
  label,
  isActive,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <Tooltip title={label}>
      <Button
        size="small"
        type={isActive ? 'primary' : 'text'}
        icon={icon}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
      />
    </Tooltip>
  )
}

export function BlogEditorToolbar({ editor }: ToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!editor) return null

  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('URL:', previous ?? 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      message.error('Dozvoljene su JPG, PNG i WebP slike')
      return
    }
    const hide = message.loading('Otpremanje slike…', 0)
    try {
      const url = await uploadBlogImage(file)
      editor.chain().focus().setImage({ src: url }).run()
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Otpremanje nije uspelo')
    } finally {
      hide()
    }
  }

  const headingItems: MenuProps['items'] = [
    { key: 'p', label: 'Paragraf' },
    { key: 'h2', label: 'Naslov H2' },
    { key: 'h3', label: 'Naslov H3' },
    { key: 'h4', label: 'Naslov H4' },
  ]

  const onHeading: MenuProps['onClick'] = ({ key }) => {
    const chain = editor.chain().focus()
    if (key === 'p') chain.setParagraph().run()
    else chain.toggleHeading({ level: Number(key.slice(1)) as 2 | 3 | 4 }).run()
  }

  const insertItems: MenuProps['items'] = [
    { key: 'image', icon: <PictureOutlined />, label: 'Slika' },
    { key: 'table', icon: <TableOutlined />, label: 'Tabela' },
    { key: 'hr', label: 'Horizontalna linija' },
    { type: 'divider' },
    { key: 'rawHtml', icon: <CodeOutlined />, label: 'Raw HTML (napredno)' },
  ]

  const onInsert: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'image':
        fileInputRef.current?.click()
        break
      case 'table':
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        break
      case 'hr':
        editor.chain().focus().setHorizontalRule().run()
        break
      case 'rawHtml':
        editor.chain().focus().insertRawHtml('').run()
        break
    }
  }

  return (
    <div className="blog-editor-toolbar">
      <Space size={2} wrap>
        <Dropdown menu={{ items: headingItems, onClick: onHeading }} trigger={['click']}>
          <Button size="small" type="text">
            Stil ▾
          </Button>
        </Dropdown>

        <Divider type="vertical" style={{ margin: '0 4px' }} />

        <MarkButton
          icon={<BoldOutlined />}
          label="Podebljano"
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <MarkButton
          icon={<ItalicOutlined />}
          label="Kurziv"
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <MarkButton
          icon={<UnderlineOutlined />}
          label="Podvučeno"
          isActive={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <MarkButton
          icon={<StrikethroughOutlined />}
          label="Precrtano"
          isActive={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />

        <Divider type="vertical" style={{ margin: '0 4px' }} />

        <MarkButton
          icon={<UnorderedListOutlined />}
          label="Lista"
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <MarkButton
          icon={<OrderedListOutlined />}
          label="Numerisana lista"
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <MarkButton
          icon={<span style={{ fontWeight: 700 }}>&rdquo;</span>}
          label="Citat"
          isActive={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <MarkButton
          icon={<LinkOutlined />}
          label="Link"
          isActive={editor.isActive('link')}
          onClick={setLink}
        />

        <Divider type="vertical" style={{ margin: '0 4px' }} />

        <Dropdown menu={{ items: insertItems, onClick: onInsert }} trigger={['click']}>
          <Button size="small" type="text" icon={<PlusOutlined />}>
            Ubaci ▾
          </Button>
        </Dropdown>
      </Space>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={onPickImage}
      />
    </div>
  )
}
